import {
  Box,
  CircularProgress,
  Typography,
  TextField,
  Stack,
} from '@mui/material';
import { useSettingsState } from '../hooks';
import { FloatingSaveButton } from '../components';

type DefaultDataEditorProps = {
  settingsPath: string;
  helperText: string;
};

// Stable reference to prevent infinite re-renders
const DEFAULT_VALUE = {};

type DefaultData = {
  patient?: {
    sex?: string;
    dateOfBirth?: string;
  };
};

const DefaultDataEditor = ({ settingsPath, helperText }: DefaultDataEditorProps) => {
  const {
    value,
    setValue,
    isDirty,
    isSaving,
    isLoading,
    isInitialized,
    save,
  } = useSettingsState<DefaultData>({
    settingsPath,
    defaultValue: DEFAULT_VALUE,
  });

  const handleSexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue({ ...value, patient: { ...value.patient, sex: e.target.value } });
  };

  const handleDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue({ ...value, patient: { ...value.patient, dateOfBirth: e.target.value } });
  };

  if (isLoading || !isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {helperText}
      </Typography>

      <Stack spacing={3} sx={{ maxWidth: 400 }}>
        <TextField
          label="Sex"
          value={value?.patient?.sex || ''}
          onChange={handleSexChange}
          helperText="Default sex value for encounters (e.g., 'U' for unknown)"
          placeholder="U"
        />

        <TextField
          label="Date of Birth"
          value={value?.patient?.dateOfBirth || ''}
          onChange={handleDateOfBirthChange}
          helperText="Default date of birth for encounters (e.g., '1900-01-01')"
          placeholder="1900-01-01"
        />
      </Stack>

      <FloatingSaveButton
        show={isDirty}
        onSave={save}
        isSaving={isSaving}
      />
    </>
  );
};

export { DefaultDataEditor };
